import React, { useState, useEffect } from "react";
import { SurveyTemplate, SurveyCategory } from "../types/Survey";
import { mockCategories, mockTemplates } from "../data/templateData";
import SurveyPreviewModal from "./SurveyPreviewModal";
import TemplateModal from "./TemplateModal";
import { renderCategoryIcon } from "../utils/categoryIcons";
import { useAuth } from "../context/AuthContext";
import { UserRole } from "../types/User";
import {
  Layout,
  Plus,
  Eye,
  FileText,
  Edit,
  Trash2,
  Search,
  Clock,
  Users,
  FileQuestion,
  Folder,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import { getBackendUrl } from "../config/env";

interface SurveyTemplateLibraryProps {
  onSelectTemplate: (template: SurveyTemplate) => void;
}

const SurveyTemplateLibrary: React.FC<SurveyTemplateLibraryProps> = ({
  onSelectTemplate,
}) => {
  const { user, centerInfo, selectedCenterId } = useAuth();
  const [categories, setCategories] = useState<SurveyCategory[]>([]);
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    template: SurveyTemplate | null;
  }>({ isOpen: false, template: null });
  const [templateModal, setTemplateModal] = useState<{
    isOpen: boolean;
    mode: "create" | "edit";
    template: any | null;
  }>({ isOpen: false, mode: "create", template: null });

  const isAdmin =
    user?.role === UserRole.SYSTEM_ADMIN ||
    user?.role === UserRole.GENERAL_ADMIN;

  useEffect(() => {
    loadTemplateData();
  }, [selectedCenterId]);

  useEffect(() => {
    const handleCenterChange = () => {
      loadTemplateData();
    };
    window.addEventListener("centerChanged", handleCenterChange);
    return () => {
      window.removeEventListener("centerChanged", handleCenterChange);
    };
  }, []);

  const loadTemplateData = async () => {
    setLoading(true);
    try {
      const centerUserId =
        selectedCenterId || user?.centerId || centerInfo?.centerId;

      // Load categories from API
      const categoriesUrl = centerUserId
        ? `${getBackendUrl()}/api/survey-categories?isActive=true&centerUserId=${centerUserId}`
                  : `${getBackendUrl()}/api/survey-categories?isActive=true`;
      const categoriesResponse = await fetch(categoriesUrl);
      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(
          categoriesData.map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            description: cat.description || "",
            icon: renderCategoryIcon(cat.icon),
            color: cat.color || "#6B7280",
          }))
        );
      } else {
        setCategories(mockCategories);
      }

      // Load templates from API
      const templatesUrl = centerUserId
        ? `${getBackendUrl()}/api/templates?centerUserId=${centerUserId}`
        : `${getBackendUrl()}/api/templates`;
      const templatesResponse = await fetch(templatesUrl);
      if (templatesResponse.ok) {
        const templatesData = await templatesResponse.json();
        setTemplates(templatesData);
      } else {
        setTemplates(mockTemplates);
      }
    } catch (error) {
      console.error("Failed to load template data:", error);
      setCategories(mockCategories);
      setTemplates(mockTemplates);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    const templateCategory = template.categoryId || template.category;
    const matchesCategory =
      selectedCategory === "all" || templateCategory === selectedCategory;
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  const handleSelectTemplate = (template: SurveyTemplate) => {
    onSelectTemplate(template);
  };

  const handlePreviewTemplate = (template: SurveyTemplate) => {
    setPreviewModal({ isOpen: true, template });
  };

  const handleClosePreview = () => {
    setPreviewModal({ isOpen: false, template: null });
  };

  const handleCreateTemplate = () => {
    const defaultTemplate =
      selectedCategory !== "all" ? { category: selectedCategory } : null;
    setTemplateModal({
      isOpen: true,
      mode: "create",
      template: defaultTemplate,
    });
  };

  const handleEditTemplate = (template: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setTemplateModal({ isOpen: true, mode: "edit", template });
  };

  const handleDeleteTemplate = async (
    templateId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (!window.confirm("정말 이 템플릿을 삭제하시겠습니까?")) {
      return;
    }

    try {
      const response = await fetch(
        `${getBackendUrl()}/api/templates/${templateId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        alert("템플릿이 삭제되었습니다.");
        loadTemplateData();
      } else {
        throw new Error("템플릿 삭제 실패");
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("템플릿 삭제에 실패했습니다.");
    }
  };

  const handleSaveTemplate = async (templateData: any) => {
    try {
      const url = templateData.id
        ? `${getBackendUrl()}/api/templates/${templateData.id}`
        : `${getBackendUrl()}/api/templates`;

      const method = templateData.id ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        alert(
          templateData.id
            ? "템플릿이 수정되었습니다."
            : "템플릿이 생성되었습니다."
        );
        setTemplateModal({ isOpen: false, mode: "create", template: null });
        loadTemplateData();
      } else {
        throw new Error("템플릿 저장 실패");
      }
    } catch (error) {
      console.error("Error saving template:", error);
      alert("템플릿 저장에 실패했습니다.");
    }
  };

  const getCategoryById = (categoryId: string) => {
    return categories.find((cat) => cat.id === categoryId);
  };

  const getEstimatedTimeText = (minutes: number) => {
    return `${minutes}분`;
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-16 items-center px-6">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Layout className="h-6 w-6" />
              <h1 className="text-2xl font-bold">템플릿 라이브러리</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              미리 만들어진 전문 템플릿으로 빠르게 설문을 시작하세요
            </p>
          </div>
          {isAdmin && (
            <Button onClick={handleCreateTemplate}>
              <Plus className="mr-2 h-4 w-4" />새 템플릿 만들기
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 border-r bg-muted/10">
          <div className="flex flex-col h-full">
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="템플릿 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-sm font-semibold mb-3">카테고리</h3>
              <div className="space-y-1">
                <button
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors",
                    selectedCategory === "all"
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  onClick={() => setSelectedCategory("all")}
                >
                  <div className="flex items-center gap-2">
                    <Folder className="h-4 w-4" />
                    <span>전체</span>
                  </div>
                  <span className="text-xs font-medium">
                    {templates.length}
                  </span>
                </button>

                {categories.map((category) => {
                  const count = templates.filter(
                    (t) => (t.categoryId || t.category) === category.id
                  ).length;
                  return (
                    <button
                      key={category.id}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors",
                        selectedCategory === category.id
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      )}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{category.icon}</span>
                        <span>{category.name}</span>
                      </div>
                      <span className="text-xs font-medium">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        {/* Templates Grid */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Mobile Search */}
            <div className="mb-6 lg:hidden">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="템플릿 이름, 설명, 태그로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Template Cards */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map((template) => {
                const category = getCategoryById(
                  template.categoryId || template.category
                );
                return (
                  <Card
                    key={template.id}
                    className="overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <Badge
                          variant="secondary"
                          style={{
                            backgroundColor: `${category?.color}20`,
                            color: category?.color,
                            borderColor: category?.color,
                          }}
                          className="border"
                        >
                          {category?.icon} {category?.name}
                        </Badge>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            <span>{template.usageCount}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>
                              {getEstimatedTimeText(template.estimatedTime)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {template.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pb-3">
                      <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                        <FileQuestion className="h-4 w-4" />
                        <span>{template.questions.length}개 질문</span>
                      </div>

                      <div className="flex flex-wrap gap-1 min-h-[24px]">
                        {template.tags && template.tags.length > 0 ? (
                          template.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground italic">태그 없음</span>
                        )}
                      </div>
                    </CardContent>

                    <CardFooter className="pt-3 border-t">
                      <div className="flex gap-2 w-full">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePreviewTemplate(template);
                          }}
                          className="flex-1"
                        >
                          <Eye className="mr-1 h-3 w-3" />
                          미리보기
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSelectTemplate(template)}
                          className="flex-1"
                        >
                          <FileText className="mr-1 h-3 w-3" />
                          템플릿 사용
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleEditTemplate(template, e)}
                              className="h-8 w-8"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) =>
                                handleDeleteTemplate(template.id, e)
                              }
                              className="h-8 w-8 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>

            {/* Empty State */}
            {filteredTemplates.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-lg font-semibold mb-2">
                  템플릿이 없습니다
                </h3>
                <p className="text-sm text-muted-foreground">
                  검색 조건을 변경하거나 다른 카테고리를 선택해보세요.
                </p>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {previewModal.template && (
        <SurveyPreviewModal
          isOpen={previewModal.isOpen}
          onClose={handleClosePreview}
          survey={previewModal.template}
          isTemplate={true}
        />
      )}

      {templateModal.isOpen && (
        <TemplateModal
          isOpen={templateModal.isOpen}
          onClose={() =>
            setTemplateModal({ isOpen: false, mode: "create", template: null })
          }
          onSave={handleSaveTemplate}
          template={templateModal.template}
          categories={categories}
          mode={templateModal.mode}
        />
      )}
    </div>
  );
};

export default SurveyTemplateLibrary;
